import React, { useState } from 'react';

interface StaffLoginProps {
  onLogin: (token: string, user: any) => void;
  error?: string | null;
  loading?: boolean;
}

const StaffLogin: React.FC<StaffLoginProps> = ({ onLogin, error, loading }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      return;
    }

    setIsSubmitting(true);
    setLoginError(null);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:2999'}/api/auth/staff-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success) {
        // Store staff session
        localStorage.setItem('yyz-cbp-token', data.token);
        localStorage.setItem('staffUser', JSON.stringify(data.user));
        
        onLogin(data.token, data.user);
      } else {
        setLoginError(data.error || 'Invalid password');
        console.error('Staff login failed:', data.error);
      }
    } catch (error) {
      setLoginError('Failed to connect to server. Please try again.');
      console.error('Staff login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#f5f5f5', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '20px' 
    }}>
      <div style={{ maxWidth: '400px', width: '100%' }}>
        <div className="card" style={{ padding: '40px' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#007bff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: 'white'
            }}>
              👥
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
              Staff Portal
            </h1>
            <p style={{ color: '#666', fontSize: '14px' }}>
              Enter staff password to access client management
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label" htmlFor="password">
                Staff Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  className="input"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter staff password"
                  disabled={isSubmitting || loading}
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting || loading}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {(error || loginError) && (
              <div className="error" style={{ marginBottom: '20px' }}>
                {error || loginError}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="button"
              disabled={!password.trim() || isSubmitting || loading}
              style={{ width: '100%', marginBottom: '20px' }}
            >
              {isSubmitting || loading ? 'Authenticating...' : 'Access Staff Portal'}
            </button>
          </form>

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <p style={{ fontSize: '12px', color: '#999' }}>
              Staff access only • YYZ CBP Commercial Invoice Generator
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffLogin;
